import { ChevronDown, Trash2 } from "lucide-react-native";
import { memo } from "react";

import type { BoardShareActionItemProps } from "./BoardShareActionItemContainer";

import {
  ASSIGNABLE_BOARD_ROLES,
  BOARD_ROLE_LABELS,
} from "@/features/player-kanban/constants/assignableBoardRoles";

import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";
import SelectDropdown, {
  type SelectDropdownOption,
} from "@/components/molecules/SelectDropdown";

import { UserKanbanBoardRole } from "@/generated/graphql";

/**
 * Builds the role options for the SelectDropdown, marking the active role.
 *
 * SE-8296: driven by `ASSIGNABLE_BOARD_ROLES`, which excludes OWNER because
 * `updateUserKanbanBoardRole` rejects that promotion outright. Ownership moves
 * through the Transfer ownership control on the share sheet instead.
 * @param currentRole - The user's current role
 * @param onSelectEditor - Editor selection handler
 * @param onSelectViewer - Viewer selection handler
 * @returns The role options in SelectDropdown shape
 */
export const buildRoleOptions = (
  currentRole: UserKanbanBoardRole,
  onSelectEditor: () => void,
  onSelectViewer: () => void
): SelectDropdownOption[] => {
  // Keyed rather than a ternary: a ternary would silently wire any third role
  // ever added to ASSIGNABLE_BOARD_ROLES to the Viewer handler. An explicit
  // lookup makes that a compile error instead.
  const handlers: Partial<Record<UserKanbanBoardRole, () => void>> = {
    [UserKanbanBoardRole.Editor]: onSelectEditor,
    [UserKanbanBoardRole.Viewer]: onSelectViewer,
  };

  return ASSIGNABLE_BOARD_ROLES.filter(role => handlers[role]).map(role => ({
    id: role.toLowerCase(),
    label: BOARD_ROLE_LABELS[role],
    selected: currentRole === role,
    onPress: handlers[role] as () => void,
  }));
};

/**
 * Props for BoardShareActionItemView component
 */
interface BoardShareActionItemViewProps extends Omit<
  BoardShareActionItemProps,
  "userKanbanBoardId" | "onRoleChange" | "onRemove" | "canManageMembers"
> {
  /** Whether the role trigger acts; false renders it as a dimmed label. */
  isRoleEditable: boolean;
  /** Whether the remove control is offered at all. */
  isRemovable: boolean;
  roleDisplayText: string;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  onSelectEditor: () => void;
  onSelectViewer: () => void;
  onRemove: () => void;
}

/**
 * View component for BoardShareActionItem - renders a single user share item UI with role selection and remove functionality.
 *
 * SE-5570: the role selector renders through the shared `SelectDropdown`
 * molecule (FixedPositionDropdownShell) instead of the retired gluestack Menu
 * atom; the active role is expressed via the row `selected` state (semantic
 * accent tokens) rather than the old data-driven Kanban highlight.
 * @param props - Component props
 * @param props.userEmail - Email address of the user who has access to the board
 * @param props.userName - Display name of the user, shown above their email
 * @param props.currentRole - Current permission role assigned to the user (Owner, Editor, or Viewer)
 * @param props.isCurrentUser - Boolean flag indicating if this item represents the currently logged-in user
 * @param props.isRoleEditable - Whether the role trigger acts on press
 * @param props.isRemovable - Whether the remove control is rendered
 * @param props.roleDisplayText - Display label for the current role (trigger text)
 * @param props.isOpen - Whether the role dropdown is open
 * @param props.onToggle - Toggle the role dropdown (trigger press)
 * @param props.onClose - Close the role dropdown
 * @param props.onSelectEditor - Callback invoked when user selects Editor role
 * @param props.onSelectViewer - Callback invoked when user selects Viewer role
 * @param props.onRemove - Callback invoked when the remove button is pressed to revoke user access
 */
const BoardShareActionItemView = ({
  userEmail,
  userName,
  currentRole,
  isCurrentUser,
  isRoleEditable,
  isRemovable,
  roleDisplayText,
  isOpen,
  onToggle,
  onClose,
  onSelectEditor,
  onSelectViewer,
  onRemove,
}: BoardShareActionItemViewProps) => (
  <Stack
    direction="horizontal"
    className="items-center justify-between border-b border-b-outline-subtle px-4 py-3"
  >
    <Stack className="min-w-0 flex-1">
      {/* "(You)" is a sibling of the name, not a third line under it: it
          qualifies who the row is, so stacking it below the email read as a
          separate fact about the user. Muted and in the same rung as the email,
          because it is an aside — it was accent-blue, the loudest colour on the
          surface, on the one row the viewer least needs to act on. */}
      <Stack direction="horizontal" className="items-baseline gap-1">
        <Text
          variant="body-strong"
          numberOfLines={1}
          className="shrink text-content-primary"
        >
          {userName || userEmail}
        </Text>
        {isCurrentUser && (
          <Text variant="caption" className="shrink-0 text-content-muted">
            (You)
          </Text>
        )}
      </Stack>
      {Boolean(userName) && (
        <Text
          variant="caption"
          numberOfLines={1}
          className="text-content-subtle"
        >
          {userEmail}
        </Text>
      )}
    </Stack>

    <Stack direction="horizontal" className="items-center gap-2">
      {isRoleEditable ? (
        <SelectDropdown
          isOpen={isOpen}
          onToggle={onToggle}
          onClose={onClose}
          anchor="right"
          triggerAccessibilityLabel="Select role"
          /* The controls family the add-user row below uses: 36px tall, an
             `outline-strong` hairline, 4px radius. */
          triggerClassName="h-9 w-28 justify-center rounded-sm border border-outline-strong bg-surface-base px-3"
          triggerContent={
            <Stack
              direction="horizontal"
              className="w-full items-center justify-between gap-2"
            >
              <Text
                variant="caption"
                numberOfLines={1}
                className="shrink text-content-primary"
              >
                {roleDisplayText}
              </Text>
              <Icon as={ChevronDown} size="xs" className="text-content-muted" />
            </Stack>
          }
          options={buildRoleOptions(
            currentRole,
            onSelectEditor,
            onSelectViewer
          )}
        />
      ) : (
        /* SE-8296: a LABEL, not a dimmed control. It used to render the
           dropdown trigger at opacity-50 with no disabled state, so a screen
           reader still announced "Select role, button" on a row where nothing
           happens. That inert set used to be one row; gating on
           canManageMembers added every row for an org admin, which is the
           case this feature exists to serve. */
        <Text
          variant="caption"
          numberOfLines={1}
          className="h-9 w-28 px-3 py-2 text-content-muted"
        >
          {roleDisplayText}
        </Text>
      )}

      {isRemovable && (
        <Pressable
          onPress={onRemove}
          accessibilityRole="button"
          accessibilityLabel={`Remove ${userName || userEmail} from board`}
          className="size-8 items-center justify-center p-0"
        >
          <Icon as={Trash2} size="xs" className="text-status-error" />
        </Pressable>
      )}
    </Stack>
  </Stack>
);

BoardShareActionItemView.displayName = "BoardShareActionItemView";

export default memo(BoardShareActionItemView);
